import React from "react";
import ReactDOM from "react-dom";

import { CloseIcon, SquareCopyIcon } from "../../components/Icons/icons";

import { writeTextToClipboardWithFallback } from "./clipboard";

/**
 * The overflow-copy popover (Figma: AI Dashboards / "Overflow copy popover", 7402:7299).
 *
 * A card that shows a value too long to read where it lives, with a Copy button and a
 * close affordance. Built as plain DOM rather than React because its first caller is an
 * AG Grid tooltip component, which AG Grid instantiates itself and hands a `getGui()`
 * contract — but the panel knows nothing about AG Grid, so anything in the dashboard
 * iframe can mount one.
 *
 * The design deliberately paints the card from the neutral gray ramp (Color/Gray/white
 * and Color/Gray/black), NOT from the surface tokens a palette tints — a berry dashboard
 * would otherwise hand the popover a pink card. So the two palettes below are fixed, and
 * the only thing that selects between them is whether the active theme is dark, which is
 * the light/dark choice the design pairs.
 *
 * Sizes are arbitrary-px utilities (`p-[16px]`, not `p-4`): the iframe root is pinned to
 * 14px, so rem-based utilities would render at ~87.5% of the designed values.
 */

/** Figma "Body/Small" 14/18 and "Label/Large" semibold 14/18, at the designed px. */
const PANEL_TEXT_CLASSES = "text-[14px] leading-[18px]";
/** Tall values scroll inside the card instead of growing it past the viewport. */
const PANEL_MAX_TEXT_HEIGHT_CLASS = "max-h-[220px]";
/** Figma Icon/Close is a 10px box; the glyph fills it. */
const CLOSE_ICON_PX = 10;
/** The copy glyph inside the design's 24px icon box, sized to sit in a 32px button. */
const COPY_ICON_PX = 16;
/** How long the button reads "Copied!" before it resets and the panel bows out. */
const COPIED_RESET_MS = 1500;

/**
 * The design's two color sets, named after the Figma variables they come from. Light is
 * Color/Gray/white on Background/background-primary; dark is Color/Gray/black with the
 * button one step lighter.
 *
 * `shadow` follows Figma shadow/small's geometry but NOT its color: the design specifies
 * an opaque Color/Gray/200, and an opaque shadow paints a grey halo over whatever sits
 * behind it. Shadows here are alpha-based so they darken the surface underneath instead,
 * which is also why dark mode only needs a heavier alpha, not a different color.
 */
interface PanelColors {
  background: string;
  foreground: string;
  buttonBackground: string;
  shadow: string;
}

const PANEL_COLORS: Record<"light" | "dark", PanelColors> = {
  light: {
    background: "#ffffff",
    foreground: "#111215",
    buttonBackground: "#f5f5f5",
    shadow: "rgba(0, 0, 0, 0.08)",
  },
  dark: {
    background: "#111215",
    foreground: "#ffffff",
    buttonBackground: "#25262a",
    shadow: "rgba(0, 0, 0, 0.45)",
  },
};

/** The iframe window carries the active dashboard theme; see DashboardWindow. */
type ThemedWindow = Window & {
  __coeffWebDashboardTheme?: {
    tailwind?: { general?: { isDarkTheme?: boolean } };
    agGrid?: { params?: { browserColorScheme?: string } };
  };
};

/**
 * Light or dark, read off the RESOLVED theme rather than its id.
 *
 * A palette theme carries the user's light/dark choice in `general.isDarkTheme`, which
 * buildThemeFromSpec sets from the spec's `mode` — the id stays the same either way.
 * Going through the id (isDarkTheme("palette:berry")) therefore answered "light" for every
 * palette dashboard, which is how a dark dashboard ended up with a white popover.
 */
function resolvePanelColors(targetWindow: Window): PanelColors {
  const theme = (targetWindow as ThemedWindow).__coeffWebDashboardTheme;
  const isDark =
    theme?.tailwind?.general?.isDarkTheme === true ||
    theme?.agGrid?.params?.browserColorScheme === "dark";
  return isDark ? PANEL_COLORS.dark : PANEL_COLORS.light;
}

/** Figma shadow/small: a tight ambient ring plus a longer drop. */
function panelShadow(colors: PanelColors): string {
  return `0 0 8px ${colors.shadow}, 0 16px 12px ${colors.shadow}`;
}

export interface CellRevealPanelOptions {
  /** The full value to display and copy. */
  text: string;
  /**
   * Renders the Copy button. Off for cases with nothing worth copying (a column header,
   * say), which also drops the panel to a single row.
   */
  showCopy?: boolean;
  /**
   * Dismisses the panel. The close button renders only when this is supplied — a caller
   * with no way to close the panel shouldn't offer an X that does nothing.
   */
  onClose?: () => void;
}

export interface CellRevealPanel {
  /** The panel root, ready to mount. */
  element: HTMLElement;
  /** Cancels pending timers. Safe to call more than once. */
  destroy: () => void;
}

/**
 * Builds the popover. `targetWindow`/`targetDocument` are the iframe's, not this module's
 * — see clipboard.ts for why they're passed explicitly.
 */
export function createCellRevealPanel(
  targetWindow: Window,
  targetDocument: Document,
  options: CellRevealPanelOptions
): CellRevealPanel {
  const { text, showCopy = true, onClose } = options;
  const colors = resolvePanelColors(targetWindow);
  let copyResetTimer = 0;

  const panel = targetDocument.createElement("div");
  panel.className =
    "coeff-cell-reveal flex w-[480px] max-w-[calc(100vw-32px)] flex-col items-start " +
    "justify-center gap-[16px] rounded-[12px] p-[16px]";
  panel.style.backgroundColor = colors.background;
  panel.style.color = colors.foreground;
  panel.style.boxShadow = panelShadow(colors);
  // AG Grid's .ag-tooltip-animate fades tooltips out over a full second. The panel is a
  // direct response to a hover or a tap, so it should go the moment it's dismissed.
  panel.style.transition = "none";

  // AG Grid routes its own hide through tooltipHideDelay, so an explicit dismiss would
  // sit on screen for another couple of seconds. Hide our element first for immediate
  // feedback, then let AG Grid tear the tooltip down on its own schedule.
  const dismiss = () => {
    panel.style.display = "none";
    onClose?.();
  };

  const topRow = targetDocument.createElement("div");
  topRow.className = "flex w-full items-start gap-[16px]";
  panel.appendChild(topRow);

  const body = targetDocument.createElement("div");
  body.className =
    `coeff-cell-reveal-text min-w-0 flex-1 overflow-y-auto ${PANEL_MAX_TEXT_HEIGHT_CLASS} ` +
    `${PANEL_TEXT_CLASSES} whitespace-pre-wrap`;
  // Wrap at word boundaries and hard-break only tokens that would overflow (the
  // break-word trio), so an id or URL stays whole until it genuinely can't fit.
  body.style.overflowWrap = "break-word";
  body.style.wordWrap = "break-word";
  body.style.wordBreak = "break-word";
  body.textContent = text;
  topRow.appendChild(body);

  let unmountCloseIcon: (() => void) | undefined;
  if (onClose) {
    const close = buildCloseButton(targetDocument, colors, dismiss);
    unmountCloseIcon = close.unmountIcon;
    topRow.appendChild(close.button);
  }

  let unmountCopyIcon: (() => void) | undefined;
  if (showCopy) {
    const copy = buildCopyButton(targetWindow, targetDocument, text, colors, {
      onCopied: dismiss,
      setResetTimer: timer => {
        copyResetTimer = timer;
      },
      clearResetTimer: () => targetWindow.clearTimeout(copyResetTimer),
    });
    unmountCopyIcon = copy.unmountIcon;
    panel.appendChild(copy.button);
  }

  return {
    element: panel,
    destroy: () => {
      targetWindow.clearTimeout(copyResetTimer);
      unmountCloseIcon?.();
      unmountCopyIcon?.();
    },
  };
}

/**
 * The X from the design — Figma's Icon/Close instance in "Overflow copy popover" is a 10x10
 * box, so the glyph keeps that size while the button spans 24px for a usable tap target. The
 * negative margin pulls the extra padding back out of the layout, so the visible X still lands
 * where the design puts it rather than pushing the text column narrower.
 *
 * Draws the shared CloseIcon rather than the Material Icons `close` ligature: it is the same X
 * every other dismissable surface uses (Modal, BlockEditPopover, DashboardHeaderEditPopover),
 * and as an SVG it simply honours width/height instead of having to out-shout the icon
 * stylesheet's `font-size: 24px`. It strokes with `currentColor`, and the panel's `color` does
 * not reach it on its own — a <button> gets `color: buttontext` from the UA stylesheet — so the
 * foreground is set on the button explicitly, the same way the Copy button does it.
 */
function buildCloseButton(
  targetDocument: Document,
  colors: PanelColors,
  onClose: () => void
): { button: HTMLButtonElement; unmountIcon: () => void } {
  const button = targetDocument.createElement("button");
  button.type = "button";
  button.className =
    "coeff-cell-reveal-close -m-[7px] flex size-[24px] shrink-0 cursor-pointer " +
    "items-center justify-center transition-opacity hover:opacity-90";
  button.style.color = colors.foreground;
  button.setAttribute("aria-label", "Close");

  const iconBox = targetDocument.createElement("span");
  iconBox.className = "flex items-center justify-center";
  ReactDOM.render(
    React.createElement(CloseIcon, { width: CLOSE_ICON_PX, height: CLOSE_ICON_PX }),
    iconBox
  );
  button.appendChild(iconBox);

  button.addEventListener("click", event => {
    event.preventDefault();
    event.stopPropagation();
    onClose();
  });

  return { button, unmountIcon: () => ReactDOM.unmountComponentAtNode(iconBox) };
}

function buildCopyButton(
  targetWindow: Window,
  targetDocument: Document,
  text: string,
  colors: PanelColors,
  timers: {
    onCopied: () => void;
    setResetTimer: (timer: number) => void;
    clearResetTimer: () => void;
  }
): { button: HTMLButtonElement; unmountIcon: () => void } {
  const button = targetDocument.createElement("button");
  button.type = "button";
  button.className =
    "coeff-cell-reveal-copy flex h-[32px] max-w-[220px] shrink-0 cursor-pointer " +
    "items-center justify-center gap-[8px] rounded-[4px] px-[12px] py-[7px] " +
    "transition-opacity hover:opacity-90";
  button.style.backgroundColor = colors.buttonBackground;
  button.style.color = colors.foreground;
  button.setAttribute("aria-label", "Copy full value");

  // The design draws the glyph inside a 24px box; keep both, rather than sizing the
  // glyph itself to 24px and blowing out the 32px button. The icon is the shared
  // SquareCopyIcon, rendered into the box because it's a React component and this panel
  // is plain DOM; `fill` overrides its brand blue so it reads as part of the label.
  const iconBox = targetDocument.createElement("span");
  iconBox.className = "flex size-[24px] shrink-0 items-center justify-center";
  ReactDOM.render(
    React.createElement(SquareCopyIcon, {
      fill: colors.foreground,
      width: COPY_ICON_PX,
      height: COPY_ICON_PX,
    }),
    iconBox
  );

  const label = targetDocument.createElement("span");
  label.className = `${PANEL_TEXT_CLASSES} font-semibold whitespace-nowrap`;
  label.textContent = "Copy";

  button.appendChild(iconBox);
  button.appendChild(label);

  // No toast is reachable from in-iframe DOM code, so the button reports its own outcome
  // (same idea as ShareDashboardModal's "Copied!" label swap).
  button.addEventListener("click", event => {
    event.preventDefault();
    event.stopPropagation();
    // An interactive AG Grid tooltip stays open while it holds focus, so a clicked Copy
    // button would pin the panel on screen even after the pointer moved away.
    button.blur();
    void writeTextToClipboardWithFallback(targetWindow, targetDocument, text)
      .then(() => {
        label.textContent = "Copied!";
      })
      .catch(() => {
        label.textContent = "Copy failed";
      })
      .finally(() => {
        timers.clearResetTimer();
        timers.setResetTimer(
          targetWindow.setTimeout(() => {
            label.textContent = "Copy";
            // The panel has confirmed the copy and served its purpose; get it off the
            // table rather than leaving it parked over the rows.
            timers.onCopied();
          }, COPIED_RESET_MS)
        );
      });
  });

  return { button, unmountIcon: () => ReactDOM.unmountComponentAtNode(iconBox) };
}

/** The slice of AG Grid's tooltip params the panel adapter reads. */
export interface CellRevealTooltipParams {
  value?: unknown;
  /** AG Grid reuses a column's tooltipComponent for its headerTooltip too. */
  location?: string;
  /** Dismisses an interactive tooltip; AG Grid passes this to custom components. */
  hideTooltipCallback?: () => void;
}

/**
 * Wraps the panel in AG Grid's tooltip-component contract (`init` / `getGui` / `destroy`).
 *
 * Assign the returned class to a COLUMN's `tooltipComponent` — on `gridOptions` AG Grid 34
 * rejects it as an unknown property and falls back to the plain text tooltip.
 */
export function createCellRevealTooltipComponent(targetWindow: Window, targetDocument: Document) {
  return class CellRevealTooltip {
    private panel: CellRevealPanel | null = null;

    init(params: CellRevealTooltipParams): void {
      const isHeader = params.location === "header";
      this.panel = createCellRevealPanel(targetWindow, targetDocument, {
        text: String(params.value ?? ""),
        // A column name is short and already visible in the header; nothing to copy.
        showCopy: !isHeader,
        onClose: params.hideTooltipCallback,
      });
    }

    getGui(): HTMLElement {
      return this.panel?.element ?? targetDocument.createElement("div");
    }

    destroy(): void {
      this.panel?.destroy();
    }
  };
}
